/**
 * Image Service Utility
 * Implements requirements 10.1-10.5: Image acquisition with fallback strategy
 * 画像取得の優先順位制御（データソース → Google Places → プレースホルダー）
 * Google Places Photos APIプロキシ
 * 帰属表示管理
 */

import { ImageData, Spot, SpotGenre } from '../types/core';
import { getCachedSpotImage } from './google-places-image';

/**
 * スポットの画像データを取得する関数（統合版）
 * @param spot - 画像取得対象のスポット
 * @param useGooglePlaces - Google Places APIを使用するか（デフォルト: true）
 * @returns 画像データ
 */
export async function getSpotImage(spot: Spot, useGooglePlaces: boolean = true): Promise<ImageData> {
    // 1. データソース提供の画像URL（最優先）
    const providerSources = [
        // Hot Pepper画像
        (spot as any).photo?.pc?.l,
        (spot as any).photo?.mobile?.l,
        // Yahoo!ローカル画像
        (spot as any).image,
        (spot as any).imageUrl,
        // 既存のspot.image
        spot.image?.url
    ].filter(Boolean);

    if (providerSources.length > 0) {
        return {
            url: providerSources[0],
            source: 'provider',
            attribution: null
        };
    }

    // 2. Google Places Photos API（有効な場合のみ）
    if (useGooglePlaces && spot.location) {
        try {
            const placesImage = await getCachedSpotImage(
                spot.name,
                spot.location,
                providerSources
            );

            if (placesImage.url && placesImage.source === 'google_places') {
                return placesImage;
            }
        } catch (error) {
            console.warn('Google Places image fetch failed:', error);
        }
    }

    // 3. プレースホルダー（最終手段）
    return {
        url: null,
        source: 'placeholder',
        attribution: null
    };
}

/**
 * 従来のAPIキーベース画像取得（後方互換性）
 * @deprecated getCachedSpotImageを使用してください
 */
export async function getSpotImageLegacy(spot: any, apiKey?: string): Promise<ImageData> {
    // 1. データソース提供の画像URL
    if (spot.imageUrl || spot.photo || spot.image) {
        const imageUrl = spot.imageUrl || spot.photo || spot.image;
        return {
            url: imageUrl,
            source: 'provider',
            attribution: null
        };
    }

    // 2. Google Places Photos API（API キーが設定されている場合のみ）
    if (apiKey && (spot.photoReference || spot.photo_reference)) {
        const photoRef = spot.photoReference || spot.photo_reference;
        return {
            url: `/api/photo?ref=${photoRef}&maxwidth=400`,
            source: 'google_places',
            attribution: '© Google'
        };
    }

    // 3. プレースホルダー
    return {
        url: null,
        source: 'placeholder',
        attribution: null
    };
}

/**
 * プレースホルダー画像のデータURLを生成
 * @param genre - スポットのジャンル
 * @param categoryJa - 日本語カテゴリ名
 * @returns プレースホルダーのデータURL
 */
export function generatePlaceholderImage(genre: SpotGenre, categoryJa: string): string {
    // SVGプレースホルダーを生成
    const icon = getCategoryIcon(genre);
    const color = getCategoryColor(genre);

    const svg = `
    <svg width="400" height="240" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="map-pattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M0 0L20 20M20 0L0 20" stroke="${color.light}" stroke-width="0.5" opacity="0.3"/>
        </pattern>
      </defs>
      <rect width="400" height="240" fill="${color.bg}"/>
      <rect width="400" height="240" fill="url(#map-pattern)"/>
      <circle cx="200" cy="120" r="40" fill="${color.main}" opacity="0.9"/>
      <text x="200" y="135" text-anchor="middle" font-family="Arial, sans-serif" font-size="32" fill="white">${icon}</text>
      <text x="200" y="180" text-anchor="middle" font-family="Arial, sans-serif" font-size="14" fill="${color.text}" font-weight="bold">${categoryJa}</text>
    </svg>
  `;

    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
}

/**
 * カテゴリ別のアイコンを取得
 * @param genre - スポットのジャンル
 * @returns アイコン文字
 */
function getCategoryIcon(genre: SpotGenre): string {
    const icons = {
        cafe: '☕',
        bookstore: '📚',
        park: '🌳',
        museum: '🏛️',
        sweets: '🍰',
        event: '🎪',
        bar: '🍺',
        restaurant: '🍽️'
    };

    return icons[genre] || '📍';
}

/**
 * カテゴリ別の色を取得
 * @param genre - スポットのジャンル
 * @returns 色情報
 */
function getCategoryColor(genre: SpotGenre): {
    main: string;
    bg: string;
    light: string;
    text: string;
} {
    const colors = {
        cafe: {
            main: '#8B4513',
            bg: '#F5E6D3',
            light: '#D2B48C',
            text: '#5D2F0A'
        },
        bookstore: {
            main: '#4A5568',
            bg: '#EDF2F7',
            light: '#A0AEC0',
            text: '#2D3748'
        },
        park: {
            main: '#38A169',
            bg: '#F0FFF4',
            light: '#9AE6B4',
            text: '#22543D'
        },
        museum: {
            main: '#805AD5',
            bg: '#FAF5FF',
            light: '#C4B5FD',
            text: '#553C9A'
        },
        sweets: {
            main: '#ED64A6',
            bg: '#FFF5F7',
            light: '#FBB6CE',
            text: '#B83280'
        },
        event: {
            main: '#F56565',
            bg: '#FFF5F5',
            light: '#FEB2B2',
            text: '#C53030'
        },
        bar: {
            main: '#DD6B20',
            bg: '#FFFAF0',
            light: '#FBD38D',
            text: '#C05621'
        },
        restaurant: {
            main: '#3182CE',
            bg: '#EBF8FF',
            light: '#90CDF4',
            text: '#2C5282'
        }
    };

    return colors[genre] || colors.restaurant;
}

/**
 * 画像URLの妥当性をチェック
 * @param url - チェック対象のURL
 * @returns 妥当性チェック結果
 */
export function validateImageUrl(url: string): boolean {
    if (!url || typeof url !== 'string') {
        return false;
    }

    try {
        const parsedUrl = new URL(url);

        // HTTPSまたはdata URLのみ許可
        if (!['https:', 'data:'].includes(parsedUrl.protocol)) {
            return false;
        }

        // 画像ファイルの拡張子チェック（data URLの場合はスキップ）
        if (parsedUrl.protocol === 'https:') {
            const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
            const pathname = parsedUrl.pathname.toLowerCase();
            const hasImageExtension = imageExtensions.some(ext => pathname.includes(ext));

            // 拡張子がない場合はAPIエンドポイントの可能性があるので許可
            return hasImageExtension || !pathname.includes('.');
        }

        return true;
    } catch {
        return false;
    }
}

/**
 * 画像の読み込み状態を管理するクラス
 */
export class ImageLoader {
    private cache = new Map<string, Promise<boolean>>();

    /**
     * 画像の読み込みを試行
     * @param url - 読み込み対象のURL
     * @returns 読み込み成功の Promise
     */
    async loadImage(url: string): Promise<boolean> {
        if (!url || !validateImageUrl(url)) {
            return false;
        }

        // キャッシュから取得
        if (this.cache.has(url)) {
            return this.cache.get(url)!;
        }

        // 新規読み込み
        const loadPromise = new Promise<boolean>((resolve) => {
            const img = new Image();

            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);

            // タイムアウト設定（5秒）
            setTimeout(() => resolve(false), 5000);

            img.src = url;
        });

        this.cache.set(url, loadPromise);
        return loadPromise;
    }

    /**
     * キャッシュをクリア
     */
    clearCache(): void {
        this.cache.clear();
    }

    /**
     * 特定のURLのキャッシュを削除
     * @param url - 削除対象のURL
     */
    removeFromCache(url: string): void {
        this.cache.delete(url);
    }
}

/**
 * 画像の最適化パラメータを生成
 * @param width - 希望幅
 * @param height - 希望高さ
 * @param quality - 品質（1-100）
 * @returns 最適化パラメータ
 */
export function generateImageOptimizationParams(
    width: number = 400,
    height: number = 240,
    quality: number = 80
): string {
    const params = new URLSearchParams({
        w: width.toString(),
        h: height.toString(),
        q: quality.toString(),
        fit: 'cover'
    });

    return params.toString();
}

/**
 * 帰属表示テキストを生成
 * @param source - 画像ソース
 * @param provider - プロバイダー名
 * @returns 帰属表示テキスト
 */
export function generateAttributionText(source: string, provider?: string): string | null {
    switch (source) {
        case 'google_places':
            return '© Google';
        case 'provider':
            return provider ? `© ${provider}` : null;
        case 'placeholder':
        default:
            return null;
    }
}

/**
 * 複数スポットの画像を並列取得
 * @param spots - スポットの配列
 * @param useGooglePlaces - Google Places APIを使用するか
 * @returns 画像データの配列
 */
export async function getBatchSpotImages(spots: Spot[], useGooglePlaces: boolean = true): Promise<ImageData[]> {
    const promises = spots.map(spot => getSpotImage(spot, useGooglePlaces));
    return Promise.all(promises);
}

/**
 * スポット画像の事前読み込み
 * @param spots - スポットの配列
 * @param useGooglePlaces - Google Places APIを使用するか
 */
export async function preloadSpotImages(spots: Spot[], useGooglePlaces: boolean = true): Promise<void> {
    try {
        const images = await getBatchSpotImages(spots, useGooglePlaces);

        // 画像の事前読み込み
        const loadPromises = images
            .filter(img => img.url && img.source !== 'placeholder')
            .map(img => imageLoader.loadImage(img.url!));

        await Promise.allSettled(loadPromises);
    } catch (error) {
        console.warn('Image preloading failed:', error);
    }
}

/**
 * 画像品質の評価
 * @param imageData - 画像データ
 * @returns 品質スコア（0-100）
 */
export function evaluateImageQuality(imageData: ImageData): number {
    if (!imageData.url) return 0;

    switch (imageData.source) {
        case 'provider':
            return 90; // Hot Pepper/Yahoo!は高品質
        case 'google_places':
            return 80; // Google Placesも高品質
        case 'placeholder':
            return 10; // プレースホルダーは低品質
        default:
            return 50;
    }
}

// シングルトンのImageLoaderインスタンス
export const imageLoader = new ImageLoader();

export default {
    getSpotImage,
    getSpotImageLegacy,
    getBatchSpotImages,
    preloadSpotImages,
    evaluateImageQuality,
    generatePlaceholderImage,
    validateImageUrl,
    ImageLoader,
    imageLoader,
    generateImageOptimizationParams,
    generateAttributionText
};